import { useMemo } from 'react';

const DEFAULT_FONT = '1rem sans-serif';

interface UseColumnWidthsOptions {
  font?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Finds the widest text value per column across ALL rows using Canvas
 * `measureText()` for ranking. Returns the actual text strings (not pixel
 * widths) so they can be rendered in a hidden element within each column
 * header. The browser then uses that content for its own natural column-width
 * calculation, producing the same widths as if all rows were rendered
 * (pre-virtualization behavior).
 *
 * Columns and rows use `any` to stay compatible with react-table's loose typings.
 */
export function useColumnWidths(
  rows: readonly any[],
  columns: readonly any[],
  options: UseColumnWidthsOptions = {},
) {
  const { font = DEFAULT_FONT, containerRef } = options;

  const widestValues = useMemo(() => {
    const result: Record<string, string> = {};
    if (!columns.length || !rows.length) return result;

    // Resolve font from the container's computed style if available.
    // On first render the ref may not be attached yet, in which case
    // we fall back to DEFAULT_FONT. This is acceptable because measureText
    // is only used for *ranking* (finding the widest string), not for
    // pixel-accurate measurement. Relative character widths are similar
    // across common sans-serif fonts.
    let resolvedFont = font;
    if (containerRef?.current) {
      const computed = getComputedStyle(containerRef.current);
      resolvedFont = `${computed.fontSize} ${computed.fontFamily}`;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'useColumnWidths: canvas.getContext("2d") returned null. ' +
            'Column width stabilization is disabled — columns may resize during scroll.',
        );
      }
      return result;
    }
    ctx.font = resolvedFont;

    for (const col of columns) {
      const colId = col.id || col.accessor || '';
      if (!colId) continue;

      let maxPx = 0;
      let widestText = '';

      for (const row of rows) {
        const value = row.values?.[colId];
        if (value == null) continue;

        const text = String(value);
        const measured = ctx.measureText(text).width;
        if (measured > maxPx) {
          maxPx = measured;
          widestText = text;
        }
      }

      if (widestText.length > 0) {
        result[colId] = widestText;
      }
    }

    return result;
    // containerRef is a stable React ref — its .current is read inside the
    // memo but the ref identity never changes, so it doesn't belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, font]);

  return { widestValues };
}
